const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function createUtcDate(input: {
  year: number;
  month: number;
  day: number;
}): Date {
  return new Date(Date.UTC(input.year, input.month - 1, input.day));
}

export function toStartOfUtcDay(inputDate: Date): Date {
  return createUtcDate({
    year: inputDate.getUTCFullYear(),
    month: inputDate.getUTCMonth() + 1,
    day: inputDate.getUTCDate(),
  });
}

export function addDays(date: Date, days: number): Date {
  const start = toStartOfUtcDay(date).getTime();
  return new Date(start + days * DAY_IN_MS);
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function diffDays(startDate: Date, endDate: Date): number {
  const start = toStartOfUtcDay(startDate).getTime();
  const end = toStartOfUtcDay(endDate).getTime();
  return Math.round((end - start) / DAY_IN_MS);
}

export function isWeekend(date: Date): boolean {
  const dayOfWeek = toStartOfUtcDay(date).getUTCDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

export function addBusinessDays(inputDate: Date, days: number, excludeWeekends: boolean): Date {
  if (!excludeWeekends || days === 0) {
    return addDays(inputDate, days);
  }

  const direction = days > 0 ? 1 : -1;
  let nextDate = toStartOfUtcDay(inputDate);
  let remainingDays = Math.abs(days);

  while (remainingDays > 0) {
    nextDate = addDays(nextDate, direction);

    if (isWeekend(nextDate)) {
      continue;
    }

    remainingDays -= 1;
  }

  return nextDate;
}

export function clampDate(date: Date, minDate: Date, maxDate: Date): Date {
  const normalized = toStartOfUtcDay(date);
  const minTime = toStartOfUtcDay(minDate).getTime();
  const maxTime = toStartOfUtcDay(maxDate).getTime();
  const dateTime = normalized.getTime();

  if (dateTime < minTime) {
    return toStartOfUtcDay(minDate);
  }

  if (dateTime > maxTime) {
    return toStartOfUtcDay(maxDate);
  }

  return normalized;
}

export function maxDate(dateA: Date, dateB: Date): Date {
  return toStartOfUtcDay(dateA).getTime() >= toStartOfUtcDay(dateB).getTime()
    ? toStartOfUtcDay(dateA)
    : toStartOfUtcDay(dateB);
}

export function minDate(dateA: Date, dateB: Date): Date {
  return toStartOfUtcDay(dateA).getTime() <= toStartOfUtcDay(dateB).getTime()
    ? toStartOfUtcDay(dateA)
    : toStartOfUtcDay(dateB);
}
